import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warehouse as WarehouseIcon,
  CategoryOutlined as CategoryIcon,
  AccountCircle as AgentIcon,
  FileDownload as FileDownloadIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import {
  fetchManagerBreakdown, 
  fetchManagerAgencyDetails,
  fetchManagerProductHierarchy,
  fetchRegionHierarchySummary,
  Metrics, 
  ManagerData,
  AgencyDetails,
  ProductHierarchy,
  ManagerBreakdownResponse,
  ProductInfo
} from '@/services/api';
import { SelectedFilters } from '@/types/filters';

interface RegionManagerDrillDownProps {
  region: string;
  initialData?: ManagerBreakdownResponse | null;
  filters?: {
    selectedMonths?: number[] | string;
    selectedYears?: number[] | string;
    selectedLOB?: string[];
    selectedProducts?: (string | ProductInfo)[];
    selectedSubProducts?: string[];
    selectedChannels?: string[];
    selectedSubChannels?: string[];
    dateRange?: { startDate: Date | null; endDate: Date | null };
    selectedRegion?: string;
  };
  compact?: boolean;
}

interface ManagerRowProps {
  manager: ManagerData;
  level?: number;
  region: string;
  onLoadSubordinates: (managerId: string, managerRole: string, hierarchyPath?: string) => Promise<ManagerData[]>;
  parentId?: string;
  expandedSubordinates?: Set<string>;
  toggleSubordinate?: (id: string) => Promise<void>;
  filters?: {
    selectedMonths?: number[];
    selectedYears?: number[];
    selectedLOB?: string[];
    selectedProducts?: string[];
    selectedSubProducts?: string[];
    selectedChannels?: string[];
    selectedSubChannels?: string[];
    dateRange?: { startDate: Date | null; endDate: Date | null };
    selectedRegion?: string[];
  };
  columnWidths?: {
    name: string;
    role?: string;
    ftmBase: string;
    ftmConv: string;
    ftmRate: string;
    aodBase: string;
    aodConv: string;
    aodRate: string;
    actions: string;
  };
}

type RoleType = 'BH' | 'VH' | 'NSM' | 'ZM' | 'RM' | 'AM' | 'SH' | 'SM';
type RoleColor = { bg: string; text: string; border: string };
type RoleColors = Record<RoleType, RoleColor>;

// Updated role-specific colors with distinct hierarchy
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#333333',
  lightBlue: '#0066CC',
  grey: '#F5F5F5',
  hover: '#004AAD',
  roleColors: {
    'BH': { bg: '#003087', text: '#FFFFFF', border: '#001F54', icon: 'business' },     // Deep Blue
    'VH': { bg: '#1A4B91', text: '#FFFFFF', border: '#003087', icon: 'business' },     // Royal Blue
    'NSM': { bg: '#5E35B1', text: '#FFFFFF', border: '#4527A0', icon: 'supervisor_account' },    // Deep Purple
    'ZM': { bg: '#0288D1', text: '#FFFFFF', border: '#01579B', icon: 'public' },     // Light Blue (was Yellow)
    'RM': { bg: '#00838F', text: '#FFFFFF', border: '#006064', icon: 'location_city' },     // Teal
    'AM': { bg: '#0277BD', text: '#FFFFFF', border: '#01579B', icon: 'map' },     // Ocean Blue
    'SH': { bg: '#6A1B9A', text: '#FFFFFF', border: '#4A148C', icon: 'store' },     // Purple
    'SM': { bg: '#607D8B', text: '#FFFFFF', border: '#455A64', icon: 'person' }      // Blue Grey
  } as RoleColors,
  productColors: {
    'LOB': { bg: '#3949AB', text: '#FFFFFF', border: '#303F9F' },     // Indigo
    'Product': { bg: '#5C6BC0', text: '#FFFFFF', border: '#3949AB' },  // Lighter Indigo
    'SubProduct': { bg: '#7986CB', text: '#FFFFFF', border: '#5C6BC0' }    // Even Lighter Indigo
  },
  channelColors: {
    'Channel': { bg: '#00ACC1', text: '#FFFFFF', border: '#0097A7' },     // Cyan
    'SubChannel': { bg: '#26C6DA', text: '#00838F', border: '#00ACC1' },  // Lighter Cyan
    'Agency': { bg: '#80DEEA', text: '#006064', border: '#26C6DA' }       // Even Lighter Cyan
  }
};

const MetricsDisplay = ({ metrics }: { metrics: Metrics }) => (
  <Grid container spacing={2}>
    <Grid item xs={6}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: `${CHOLA_COLORS.primary}08`,
          border: `1px solid ${CHOLA_COLORS.primary}20`
        }}
      >
        <Typography variant="subtitle2" sx={{ color: CHOLA_COLORS.primary, mb: 1 }}>FTM Performance</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Total Base:</Typography>
          <Typography variant="body2" fontWeight={500}>{metrics.ftm.total_base}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Converted:</Typography>
          <Typography variant="body2" fontWeight={500}>{metrics.ftm.conversion}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Rate:</Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: metrics.ftm.conv_rate >= 70 ? '#4CAF50' : 
                     metrics.ftm.conv_rate >= 40 ? '#FF6E00' : '#F44336'
            }}
          >
            {metrics.ftm.conv_rate.toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Grid>
    <Grid item xs={6}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: `#5E35B108`,
          border: `1px solid #5E35B120`
        }}
      >
        <Typography variant="subtitle2" sx={{ color: '#5E35B1', mb: 1 }}>AOD Performance</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Total Base:</Typography>
          <Typography variant="body2" fontWeight={500}>{metrics.aod.total_base}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Converted:</Typography>
          <Typography variant="body2" fontWeight={500}>{metrics.aod.conversion}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Rate:</Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: metrics.aod.conv_rate >= 70 ? '#4CAF50' : 
                     metrics.aod.conv_rate >= 40 ? '#FF6E00' : '#F44336'
            }}
          >
            {metrics.aod.conv_rate.toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Grid>
  </Grid>
);

const getConversionColor = (rate: number, theme: any) => {
  if (rate >= 70) return '#4CAF50'; // Green for 70% and above
  if (rate >= 40) return '#FF6E00'; // Orange for 40-69%
  return '#F44336'; // Red for less than 40%
};

const ChannelRow = ({ 
  name, 
  metrics, 
  level, 
  type,
  children 
}: { 
  name: string;
  metrics: Metrics;
  level: number;
  type: 'Channel' | 'SubChannel' | 'Agency';
  children?: { [key: string]: any };
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = children && Object.keys(children).length > 0;
  
  // Use the channel-specific colors
  const channelColor = CHOLA_COLORS.channelColors[type] || { 
    bg: theme.palette.primary.light, 
    text: theme.palette.primary.dark, 
    border: theme.palette.primary.main 
  };

  // Define fixed column widths for channel tables
  const channelColumnWidths = {
    name: '25%',
    ftmBase: '12.5%',
    ftmConv: '12.5%',
    ftmRate: '12.5%',
    aodBase: '12.5%',
    aodConv: '12.5%',
    aodRate: '12.5%'
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { bgcolor: `${channelColor.bg}08` },
          cursor: hasChildren ? 'pointer' : 'default',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        <TableCell sx={{ pl: level * 2 + 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  transition: 'transform 0.3s ease',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: channelColor.border,
                  p: 0.25,
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ width: 20 }} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Typography 
                variant="body2" 
                fontWeight={type === 'Channel' ? 500 : 400}
                sx={{ 
                  color: 'text.primary',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal'
                }}
              >
                {name}
              </Typography>
            </Box>
            <Chip
              label={type}
              size="small"
              sx={{
                bgcolor: channelColor.bg,
                color: channelColor.text,
                fontWeight: 500,
                fontSize: '0.7rem',
                height: '20px',
                borderRadius: '4px',
                flexShrink: 0,
                '& .MuiChip-label': {
                  px: 1,
                  py: 0
                }
              }}
            />
          </Box>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {metrics.ftm.total_base.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {metrics.ftm.conversion.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(metrics.ftm.conv_rate, theme),
              fontWeight: 600
            }}
          >
            {metrics.ftm.conv_rate.toFixed(1)}%
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {metrics.aod.total_base.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {metrics.aod.conversion.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(metrics.aod.conv_rate, theme),
              fontWeight: 600
            }}
          >
            {metrics.aod.conv_rate.toFixed(1)}%
          </Typography>
        </TableCell>
      </TableRow>

      {expanded && hasChildren && (
        <TableRow>
          <TableCell 
            colSpan={7} 
            sx={{ 
              p: 0, 
              border: 'none',
              bgcolor: `${channelColor.bg}04`,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: level * 2 + 28,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: `${channelColor.border}40`
              }
            }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Table size="small" sx={{ 
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    px: 1,
                    py: 0.75,
                    borderColor: theme.palette.divider,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  },
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                  },
                  '& .MuiTableCell-root:first-of-type': {
                    borderLeft: 'none',
                  },
                  '& .MuiTableCell-root:last-of-type': {
                    borderRight: 'none',
                  }
                }}>
                  <colgroup>
                    <col style={{ width: channelColumnWidths.name }} />
                    <col style={{ width: channelColumnWidths.ftmBase }} />
                    <col style={{ width: channelColumnWidths.ftmConv }} />
                    <col style={{ width: channelColumnWidths.ftmRate }} />
                    <col style={{ width: channelColumnWidths.aodBase }} />
                    <col style={{ width: channelColumnWidths.aodConv }} />
                    <col style={{ width: channelColumnWidths.aodRate }} />
                  </colgroup>
                  <TableBody>
                    {Object.entries(children).map(([childName, childData]) => (
                      <ChannelRow
                        key={childName}
                        name={childData.name}
                        metrics={childData.metrics}
                        level={level + 1}
                        type={type === 'Channel' ? 'SubChannel' : 'Agency'}
                        children={childData.subchannels || childData.agencies}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AgencyDialog = ({ open, onClose, agencyDetails }: { 
  open: boolean; 
  onClose: () => void; 
  agencyDetails: AgencyDetails | null;
}) => {
  const theme = useTheme();
  
  if (!agencyDetails) return null;

  // Define fixed column widths for agency dialog
  const channelColumnWidths = {
    name: '25%',
    ftmBase: '12.5%',
    ftmConv: '12.5%',
    ftmRate: '12.5%',
    aodBase: '12.5%',
    aodConv: '12.5%',
    aodRate: '12.5%'
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
          bgcolor: 'background.paper'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="h2">
            Channel Performance Matrix
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <ExpandLessIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ height: 'auto', width: '100%' }}>
          <TableContainer>
            <Table sx={{ 
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                px: 1,
                py: 1,
                borderColor: theme.palette.divider,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                borderTop: 'none',
              },
              '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
              '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTableCell-root:first-of-type': {
                borderLeft: 'none',
              },
              '& .MuiTableCell-root:last-of-type': {
                borderRight: 'none',
              }
            }}>
              <colgroup>
                <col style={{ width: channelColumnWidths.name }} />
                <col style={{ width: channelColumnWidths.ftmBase }} />
                <col style={{ width: channelColumnWidths.ftmConv }} />
                <col style={{ width: channelColumnWidths.ftmRate }} />
                <col style={{ width: channelColumnWidths.aodBase }} />
                <col style={{ width: channelColumnWidths.aodConv }} />
                <col style={{ width: channelColumnWidths.aodRate }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell 
                    rowSpan={2}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell 
                    align="center"
                    colSpan={3}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    FTM
                  </TableCell>
                  <TableCell 
                    align="center"
                    colSpan={3}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    AOD
                  </TableCell>
                </TableRow>
                <TableRow>
                  {/* Total Base, Converted, Conv. Rate subheaders for FTM */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Total Base
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Converted
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Conv. Rate
                  </TableCell>
                  
                  {/* Total Base, Converted, Conv. Rate subheaders for AOD */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Total Base
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Converted
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Conv. Rate
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(agencyDetails.channels || {}).map(([channelName, data]) => (
                  <ChannelRow
                    key={channelName}
                    name={channelName}
                    metrics={data.metrics}
                    level={0}
                    type="Channel"
                    children={data.subchannels}
                  />
                ))}
                
                {/* Total Row for all Channels */}
                {Object.keys(agencyDetails.channels || {}).length > 0 && (
                  <TableRow 
                    sx={{ 
                      bgcolor: theme.palette.grey[50],
                      borderTop: `2px solid ${theme.palette.divider}`,
                      '& .MuiTableCell-root': {
                        fontWeight: 'bold',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: theme.palette.grey[50],
                        zIndex: 1,
                        py: 1.5,
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        TOTAL (All Channels)
                      </Typography>
                    </TableCell>
                    
                    {/* FTM Metrics */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {Object.values(agencyDetails.channels || {})
                          .reduce((sum, channel) => sum + channel.metrics.ftm.total_base, 0)
                          .toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {Object.values(agencyDetails.channels || {})
                          .reduce((sum, channel) => sum + channel.metrics.ftm.conversion, 0)
                          .toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const totalBase = Object.values(agencyDetails.channels || {})
                          .reduce((sum, channel) => sum + channel.metrics.ftm.total_base, 0);
                        const totalConversion = Object.values(agencyDetails.channels || {})
                          .reduce((sum, channel) => sum + channel.metrics.ftm.conversion, 0);
                        const convRate = totalBase > 0 ? (totalConversion / totalBase * 100) : 0;
                        return (
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            sx={{ 
                              color: getConversionColor(convRate, theme)
                            }}
                          >
                            {convRate.toFixed(1)}%
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    
                    {/* AOD Metrics */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {Object.values(agencyDetails.channels || {})
                          .reduce((sum, channel) => sum + channel.metrics.aod.total_base, 0)
                          .toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {Object.values(agencyDetails.channels || {})
                          .reduce((sum, channel) => sum + channel.metrics.aod.conversion, 0)
                          .toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const totalBase = Object.values(agencyDetails.channels || {})
                          .reduce((sum, channel) => sum + channel.metrics.aod.total_base, 0);
                        const totalConversion = Object.values(agencyDetails.channels || {})
                          .reduce((sum, channel) => sum + channel.metrics.aod.conversion, 0);
                        const convRate = totalBase > 0 ? (totalConversion / totalBase * 100) : 0;
                        return (
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            sx={{ 
                              color: getConversionColor(convRate, theme)
                            }}
                          >
                            {convRate.toFixed(1)}%
                          </Typography>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const ProductRow = ({ 
  name, 
  metrics, 
  level, 
  type,
  children 
}: { 
  name: string;
  metrics: Metrics;
  level: number;
  type: 'LOB' | 'Product' | 'SubProduct';
  children?: { [key: string]: any };
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = children && Object.keys(children).length > 0;
  
  // Use the product-specific colors
  const productColor = CHOLA_COLORS.productColors[type] || { 
    bg: theme.palette.primary.light, 
    text: theme.palette.primary.dark, 
    border: theme.palette.primary.main 
  };

  // Define fixed column widths for product tables
  const productColumnWidths = {
    name: '25%',
    ftmBase: '12.5%',
    ftmConv: '12.5%',
    ftmRate: '12.5%',
    aodBase: '12.5%',
    aodConv: '12.5%',
    aodRate: '12.5%'
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { bgcolor: `${productColor.bg}08` },
          cursor: hasChildren ? 'pointer' : 'default',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        <TableCell sx={{ pl: level * 2 + 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  transition: 'transform 0.3s ease',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: productColor.border,
                  p: 0.25,
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ width: 20 }} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Typography 
                variant="body2" 
                fontWeight={type === 'SubProduct' ? 400 : 500}
                sx={{ 
                  color: 'text.primary',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal'
                }}
              >
                {name || 'No Group'}
              </Typography>
            </Box>
            <Chip
              label={type === 'LOB' ? 'Product Group' : type}
              size="small"
              sx={{
                bgcolor: productColor.bg,
                color: productColor.text,
                fontWeight: 500,
                fontSize: '0.7rem',
                height: '20px',
                borderRadius: '4px',
                flexShrink: 0,
                '& .MuiChip-label': {
                  px: 1,
                  py: 0
                }
              }}
            />
          </Box>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {metrics.ftm.total_base.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {metrics.ftm.conversion.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(metrics.ftm.conv_rate, theme),
              fontWeight: 600
            }}
          >
            {metrics.ftm.conv_rate.toFixed(1)}%
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {metrics.aod.total_base.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {metrics.aod.conversion.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(metrics.aod.conv_rate, theme),
              fontWeight: 600
            }}
          >
            {metrics.aod.conv_rate.toFixed(1)}%
          </Typography>
        </TableCell>
      </TableRow>

      {expanded && hasChildren && (
        <TableRow>
          <TableCell 
            colSpan={7} 
            sx={{ 
              p: 0, 
              border: 'none',
              bgcolor: `${productColor.bg}04`,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: level * 2 + 28,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: `${productColor.border}40`
              }
            }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Table size="small" sx={{ 
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    px: 1,
                    py: 0.75,
                    borderColor: theme.palette.divider,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  },
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                  },
                  '& .MuiTableCell-root:first-of-type': {
                    borderLeft: 'none',
                  },
                  '& .MuiTableCell-root:last-of-type': {
                    borderRight: 'none',
                  }
                }}>
                  <colgroup>
                    <col style={{ width: productColumnWidths.name }} />
                    <col style={{ width: productColumnWidths.ftmBase }} />
                    <col style={{ width: productColumnWidths.ftmConv }} />
                    <col style={{ width: productColumnWidths.ftmRate }} />
                    <col style={{ width: productColumnWidths.aodBase }} />
                    <col style={{ width: productColumnWidths.aodConv }} />
                    <col style={{ width: productColumnWidths.aodRate }} />
                  </colgroup>
                  <TableBody>
                    {Object.entries(children).map(([childName, childData]) => (
                      <ProductRow
                        key={childName}
                        name={childName}
                        metrics={childData.metrics}
                        level={level + 1}
                        type="SubProduct"
                        children={{}} // Pass empty object instead of null
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ProductDialog = ({ 
  open, 
  onClose, 
  productData 
}: { 
  open: boolean; 
  onClose: () => void; 
  productData: { [key: string]: ProductHierarchy } | null;
}) => {
  const theme = useTheme();
  
  if (!productData) return null;

  // Define fixed column widths for product dialog
  const productColumnWidths = {
    name: '25%',
    ftmBase: '12.5%',
    ftmConv: '12.5%',
    ftmRate: '12.5%',
    aodBase: '12.5%',
    aodConv: '12.5%',
    aodRate: '12.5%'
  };

  // Calculate totals for all product groups
  const calculateTotals = () => {
    let ftmTotalBase = 0;
    let ftmConversion = 0;
    let aodTotalBase = 0;
    let aodConversion = 0;

    Object.values(productData).forEach(group => {
      ftmTotalBase += group.metrics.ftm.total_base;
      ftmConversion += group.metrics.ftm.conversion;
      aodTotalBase += group.metrics.aod.total_base;
      aodConversion += group.metrics.aod.conversion;
    });

    const ftmConvRate = ftmTotalBase > 0 ? (ftmConversion / ftmTotalBase * 100) : 0;
    const aodConvRate = aodTotalBase > 0 ? (aodConversion / aodTotalBase * 100) : 0;

    return {
      ftm: {
        total_base: ftmTotalBase,
        conversion: ftmConversion,
        conv_rate: ftmConvRate
      },
      aod: {
        total_base: aodTotalBase,
        conversion: aodConversion,
        conv_rate: aodConvRate
      }
    };
  };

  const totalMetrics = calculateTotals();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
          bgcolor: 'background.paper'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="h2">
            Product Performance Matrix
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <ExpandLessIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ height: 'auto', width: '100%' }}>
          <TableContainer>
            <Table sx={{ 
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                px: 1,
                py: 1,
                borderColor: theme.palette.divider,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                borderTop: 'none',
              },
              '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
              '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTableCell-root:first-of-type': {
                borderLeft: 'none',
              },
              '& .MuiTableCell-root:last-of-type': {
                borderRight: 'none',
              }
            }}>
              <colgroup>
                <col style={{ width: productColumnWidths.name }} />
                <col style={{ width: productColumnWidths.ftmBase }} />
                <col style={{ width: productColumnWidths.ftmConv }} />
                <col style={{ width: productColumnWidths.ftmRate }} />
                <col style={{ width: productColumnWidths.aodBase }} />
                <col style={{ width: productColumnWidths.aodConv }} />
                <col style={{ width: productColumnWidths.aodRate }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell 
                    rowSpan={2}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell 
                    align="center"
                    colSpan={3}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    FTM
                  </TableCell>
                  <TableCell 
                    align="center"
                    colSpan={3}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    AOD
                  </TableCell>
                </TableRow>
                <TableRow>
                  {/* Total Base, Converted, Conv. Rate subheaders for FTM */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Total Base
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Converted
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Conv. Rate
                  </TableCell>
                  
                  {/* Total Base, Converted, Conv. Rate subheaders for AOD */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Total Base
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Converted
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Conv. Rate
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Display product groups with their sub_products */}
                {Object.entries(productData).map(([groupName, groupData]) => (
                  <ProductRow
                    key={groupName}
                    name={groupName}
                    metrics={groupData.metrics}
                    level={0}
                    type="LOB"
                    children={groupData.products}
                  />
                ))}
                
                {/* Total Row for all Products */}
                {Object.keys(productData).length > 0 && (
                  <TableRow 
                    sx={{ 
                      bgcolor: theme.palette.grey[50],
                      borderTop: `2px solid ${theme.palette.divider}`,
                      '& .MuiTableCell-root': {
                        fontWeight: 'bold',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: theme.palette.grey[50],
                        zIndex: 1,
                        py: 1.5,
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        TOTAL (All Product Groups)
                      </Typography>
                    </TableCell>
                    
                    {/* FTM Metrics */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {totalMetrics.ftm.total_base.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {totalMetrics.ftm.conversion.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          color: getConversionColor(totalMetrics.ftm.conv_rate, theme)
                        }}
                      >
                        {totalMetrics.ftm.conv_rate.toFixed(1)}%
                      </Typography>
                    </TableCell>
                    
                    {/* AOD Metrics */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {totalMetrics.aod.total_base.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {totalMetrics.aod.conversion.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          color: getConversionColor(totalMetrics.aod.conv_rate, theme)
                        }}
                      >
                        {totalMetrics.aod.conv_rate.toFixed(1)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const getRoleFullForm = (role: string): string => {
  const roleMap: Record<string, string> = {
    'BH': 'Business Head',
    'VH': 'Vertical Head',
    'NSM': 'National Sales Manager',
    'ZM': 'Zonal Manager',
    'RM': 'Regional Manager',
    'AM': 'Area Manager',
    'SH': 'Sales Head',
    'SM': 'Sales Manager'
  };
  return roleMap[role] || role;
};

// Get role color from either short code or full name
const getRoleColor = (role: string): string => {
  // Check if role is already a short code (BH, VH, etc.)
  if (CHOLA_COLORS.roleColors[role.toUpperCase() as RoleType]?.bg) {
    return CHOLA_COLORS.roleColors[role.toUpperCase() as RoleType].bg;
  }
  
  // Map full role names to short codes
  const roleMapToShortCode: Record<string, RoleType> = {
    'BUSINESS HEAD': 'BH',
    'VERTICAL HEAD': 'VH',
    'NATIONAL SALES MANAGER': 'NSM',
    'ZONAL MANAGER': 'ZM',
    'REGIONAL MANAGER': 'RM',
    'AREA MANAGER': 'AM',
    'SALES HEAD': 'SH',
    'SALES MANAGER': 'SM'
  };
  
  // Convert to uppercase to handle mixed case names
  const upperRole = role.toUpperCase();
  
  const shortCode = roleMapToShortCode[upperRole];

  // Return the color from the short code or default color
  return shortCode 
    ? CHOLA_COLORS.roleColors[shortCode].bg 
    : '#1976d2'; // Default blue
};

const getRoleStyle = (role: string, level: number) => {
  // Get role-specific styling
  const isValidRole = (r: string): r is RoleType => 
    ['BH', 'VH', 'NSM', 'ZM', 'RM', 'AM', 'SH', 'SM'].includes(r);
  
  const roleColor = isValidRole(role) 
    ? CHOLA_COLORS.roleColors[role]
    : { bg: CHOLA_COLORS.grey, text: CHOLA_COLORS.text, border: CHOLA_COLORS.grey, icon: 'person' };


    
  
  return {
    row: {
      bgcolor: 'background.paper',
      '&:hover': {
        bgcolor: `${roleColor.bg}08`
      },
      borderLeft: `4px solid ${roleColor.border}`,
      transition: 'all 0.2s ease',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: '1px',
        bgcolor: `${roleColor.border}20`
      }
    },
    nameCell: {
      pl: 2 + (level * 3),
      py: role === 'BH' ? 2.5 : role === 'SM' ? 1.5 : 2,
      borderBottom: 'none',
      width: '30%',
      whiteSpace: 'nowrap'
    },
    roleChip: {
      bgcolor: roleColor.bg,
      color: roleColor.text,
      fontWeight: 500,
      fontSize: role === 'SM' ? '0.75rem' : '0.8125rem',
      height: role === 'SM' ? '24px' : '32px',
      border: `1px solid ${roleColor.border}`,
      boxShadow: role === 'SM' ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
      '& .MuiChip-label': {
        px: 2,
        fontWeight: role === 'BH' || role === 'VH' ? 600 : 500
      }
    },
    expandButton: (expanded: boolean) => ({
      color: roleColor.bg,
      bgcolor: expanded ? `${roleColor.bg}15` : 'transparent',
      border: `1px solid ${roleColor.border}40`,
      '&:hover': {
        bgcolor: `${roleColor.bg}25`
      }
    }),
    metrics: {
      color: roleColor.text === '#FFFFFF' ? roleColor.bg : roleColor.text,
      fontWeight: role === 'BH' || role === 'VH' ? 600 : 500
    },
    expandedSection: {
      p: 0, 
      border: 'none',
      bgcolor: `${roleColor.bg}04`,
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: level * 3 + 28,
        top: 0,
        bottom: 0,
        width: 2,
        bgcolor: `${roleColor.border}40`
      }
    },
    actionButton: {
      color: roleColor.text === '#FFFFFF' ? roleColor.bg : roleColor.text,
      border: `1px solid ${roleColor.border}40`,
      '&:hover': {
        bgcolor: `${roleColor.bg}15`
      }
    }
  };
};

// Add this function before the ManagerRow component
const getRoleShortForm = (role: string): string => {
  // Convert to uppercase first to handle mixed case
  const upperRole = role.toUpperCase();
  // Map full role names to short forms if needed
  const roleMap: Record<string, string> = {
    'BUSINESS HEAD': 'bh',
    'VERTICAL HEAD': 'vh',
    'NATIONAL SALES MANAGER': 'nsm',
    'ZONAL MANAGER': 'zm',
    'REGIONAL MANAGER': 'rm',
    'AREA MANAGER': 'am',
    'SALES HEAD': 'sh',
    'SALES MANAGER': 'sm',
    'FIELD LEVEL SUPERVISOR': 'fls'
  };
  
  // If it's already a short form (BH, VH, etc.), just return lowercase
  if (['BH', 'VH', 'NSM', 'ZM', 'RM', 'AM', 'SH', 'SM', 'FLS'].includes(upperRole)) {
    return upperRole.toLowerCase();
  }
  
  // Otherwise, try to map from full name to short form
  return roleMap[upperRole] || upperRole.toLowerCase();
};

const ManagerRow = ({
  manager,
  level = 0,
  region,
  onLoadSubordinates,
  parentId,
  expandedSubordinates,
  toggleSubordinate,
  filters,
  columnWidths
}: ManagerRowProps): React.ReactElement => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [subordinates, setSubordinates] = useState<ManagerData[]>([]);
  const [showAgency, setShowAgency] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [agencyDetails, setAgencyDetails] = useState<AgencyDetails | null>(null);
  const [productData, setProductData] = useState<{ [key: string]: ProductHierarchy } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Check if this manager is expanded using the parent's state
  const isExpanded = expandedSubordinates?.has(manager.id) || false;

  // Determine if this manager type can expand (based on role hierarchy)
  const canExpand = manager.role !== 'SM';
  
  // Get the role-specific styling
  // Convert full role names to short forms for proper typing
  // manager.role could be like "Vertical Head" or "VH"
  const normalizedRole = manager.role.toUpperCase();
  const roleMap: Record<string, RoleType> = {
    'BUSINESS HEAD': 'BH',
    'VERTICAL HEAD': 'VH',
    'NATIONAL SALES MANAGER': 'NSM',
    'ZONAL MANAGER': 'ZM',
    'REGIONAL MANAGER': 'RM',
    'AREA MANAGER': 'AM',
    'SALES HEAD': 'SH',
    'SALES MANAGER': 'SM',
    'FIELD LEVEL SUPERVISOR': 'SH' // Map FLS to SH to avoid type error
  };
  
  // Use the mapped role if it exists, otherwise use the original if it's already a short form
  const roleType = (roleMap[normalizedRole] || 
    (['BH', 'VH', 'NSM', 'ZM', 'RM', 'AM', 'SH', 'SM'].includes(normalizedRole) ? normalizedRole : 'SM')) as RoleType;
  
  const roleColor = CHOLA_COLORS.roleColors[roleType] || { 
    bg: theme.palette.primary.light, 
    text: theme.palette.primary.dark, 
    border: theme.palette.primary.main 
  };

  const handleExpand = async () => {
    if (!canExpand || !toggleSubordinate) return;
    
    
    
    // If expanding and we don't have subordinates yet, load them
    if (!isExpanded && subordinates.length === 0) {
      setLoading(true);
      setError(null);
      try {
        // Build hierarchy path
        let hierarchyPath = manager.hierarchy_path || '';
        
        // If no existing hierarchy path, create one starting with this manager
        if (!hierarchyPath) {
          hierarchyPath = `${manager.role}:${manager.id}`;
        }
        
        // Use onLoadSubordinates to load the subordinates
        const subordinatesData = await onLoadSubordinates(manager.id, manager.role, hierarchyPath);
        setSubordinates(subordinatesData);
      } catch (err) {
        console.error('Error loading subordinates:', err);
        setError('Failed to load subordinates');
      } finally {
        setLoading(false);
      }
    }
    
    // Use the parent's toggle function to update the expanded state
    await toggleSubordinate(manager.id);
    
  };

  // Update the loadAgencyDetails function with proper transformedChannels typing
  const loadAgencyDetails = async () => {
    try {
      // Use hierarchy path if available
      const hierarchyPath = manager.hierarchy_path || `${manager.role}:${manager.id}`;

      // Prepare the correct filter structure for API
      const apiFilters: any = {};
      
      if (filters) {
        // Convert selectedMonths to string format expected by buildFilterParams
        if (filters.selectedMonths) {
          if (Array.isArray(filters.selectedMonths)) {
            apiFilters.selectedMonth = filters.selectedMonths[0]?.toString() || '';
          } else {
            apiFilters.selectedMonth = String(filters.selectedMonths);
          }
        }
        
        // Convert selectedYears to string format expected by buildFilterParams
        if (filters.selectedYears) {
          if (Array.isArray(filters.selectedYears)) {
            apiFilters.selectedYear = filters.selectedYears[0]?.toString() || '';
          } else {
            apiFilters.selectedYear = String(filters.selectedYears);
          }
        }
        
        // Add other filters
        if (filters.selectedLOB) apiFilters.selectedLOB = filters.selectedLOB;
        if (filters.selectedProducts) apiFilters.selectedProducts = filters.selectedProducts;
        if (filters.selectedChannels) apiFilters.selectedChannels = filters.selectedChannels;
        if (filters.selectedSubChannels) apiFilters.selectedSubChannels = filters.selectedSubChannels;
        if (filters.dateRange) apiFilters.dateRange = filters.dateRange;
        if (filters.selectedSubProducts) apiFilters.selectedSubProducts = filters.selectedSubProducts;
        if (filters.selectedRegion) apiFilters.selectedRegion = filters.selectedRegion;

      }

      // Get the manager-breakdown API response with channels data
      const data = await fetchManagerBreakdown(
        manager.id,
        getRoleShortForm(manager.role),
        hierarchyPath,
        apiFilters
      );
      
      // Transform response data to match the expected format for AgencyDialog
      const transformedChannels: AgencyDetails['channels'] = {};
      
      // Convert channels array to the format expected by AgencyDialog
      if (data.channels && data.channels.length > 0) {
        data.channels.forEach((channel: any) => {
          const channelName = channel.channel;
          transformedChannels[channelName] = {
            name: channelName,
            metrics: {
              ftm: {
                total_base: Number(channel.ftm.total_leads),
                conversion: Number(channel.ftm.converted),
                conv_rate: Number(channel.ftm.conversion_rate)
              },
              aod: {
                total_base: Number(channel.aod.total_leads),
                conversion: Number(channel.aod.converted),
                conv_rate: Number(channel.aod.conversion_rate)
              }
            },
            subchannels: {}
          };
          
          // Add subchannels if they exist - checking both the new and old format
          if (channel.sub_channels) {
            // If sub_channels is an object (new format), convert to proper format
            if (typeof channel.sub_channels === 'object' && !Array.isArray(channel.sub_channels)) {
              Object.entries(channel.sub_channels).forEach(([subchannelKey, subchannelData]: [string, any]) => {
                transformedChannels[channelName].subchannels[subchannelKey] = {
                  name: subchannelData.subchannel,
                  metrics: {
                    ftm: {
                      total_base: Number(subchannelData.ftm.total_leads),
                      conversion: Number(subchannelData.ftm.converted),
                      conv_rate: Number(subchannelData.ftm.conversion_rate)
                    },
                    aod: {
                      total_base: Number(subchannelData.aod.total_leads),
                      conversion: Number(subchannelData.aod.converted),
                      conv_rate: Number(subchannelData.aod.conversion_rate)
                    }
                  },
                  agencies: {} // Required by the type but not used in this context
                };
              });
            } 
            // If sub_channels is an array (old format), use the array format
            else if (Array.isArray(channel.sub_channels)) {
              channel.sub_channels.forEach((subchannel: any) => {
                const subchannelName = subchannel.subchannel;
                transformedChannels[channelName].subchannels[subchannelName] = {
                  name: subchannelName,
                  metrics: {
                    ftm: {
                      total_base: Number(subchannel.ftm.total_leads),
                      conversion: Number(subchannel.ftm.converted),
                      conv_rate: Number(subchannel.ftm.conversion_rate)
                    },
                    aod: {
                      total_base: Number(subchannel.aod.total_leads),
                      conversion: Number(subchannel.aod.converted),
                      conv_rate: Number(subchannel.aod.conversion_rate)
                    }
                  },
                  agencies: {} // Required by the type but not used in this context
                };
              });
            }
          }
        });
      }
      
      const transformedData: AgencyDetails = {
        channels: transformedChannels,
        hierarchy: {
          fls: { id: '', name: '' },
          sm: { id: '', name: '' },
          sh: { id: '', name: '' },
          am: { id: '', name: '' },
          rm: { id: '', name: '' },
          zm: { id: '', name: '' },
          nsm: { id: '', name: '' },
          vh: { id: '', name: '' },
          bh: { id: '', name: '' }
        },
        location: {
          partner_region: '',
          partner_zone: '',
          chola_branch: '',
          chola_cluster: '',
          chola_state: ''
        }
      };
      
      setAgencyDetails(transformedData);
      setShowAgency(true);
    } catch (error) {
      console.error('Error loading agency details:', error);
      // Show error in UI if needed
    }
  };

  const loadProductDetails = async () => {
    try {
      // Use hierarchy path if available
      const hierarchyPath = manager.hierarchy_path || `${manager.role}:${manager.id}`;
      
      // Prepare the correct filter structure for API
      const apiFilters: any = {};
      
      if (filters) {
        // Convert selectedMonths to string format expected by buildFilterParams
        if (filters.selectedMonths) {
          if (Array.isArray(filters.selectedMonths)) {
            apiFilters.selectedMonth = filters.selectedMonths[0]?.toString() || '';
          } else {
            apiFilters.selectedMonth = String(filters.selectedMonths);
          }
        }
        
        // Convert selectedYears to string format expected by buildFilterParams
        if (filters.selectedYears) {
          if (Array.isArray(filters.selectedYears)) {
            apiFilters.selectedYear = filters.selectedYears[0]?.toString() || '';
          } else {
            apiFilters.selectedYear = String(filters.selectedYears);
          }
        }
        
        // Add other filters
        if (filters.selectedLOB) apiFilters.selectedLOB = filters.selectedLOB;
        if (filters.selectedProducts) apiFilters.selectedProducts = filters.selectedProducts;
        if (filters.selectedChannels) apiFilters.selectedChannels = filters.selectedChannels;
        if (filters.selectedSubChannels) apiFilters.selectedSubChannels = filters.selectedSubChannels;
        if (filters.dateRange) apiFilters.dateRange = filters.dateRange;
        if (filters.selectedRegion) apiFilters.selectedRegion = filters.selectedRegion;
        if (filters.selectedSubProducts) apiFilters.selectedSubProducts = filters.selectedSubProducts;
      }
      
      // Get the manager-breakdown API response with products data
      const data = await fetchManagerBreakdown(
        manager.id,
        getRoleShortForm(manager.role),
        hierarchyPath,
        apiFilters
      );

      // Transform the products array to the format expected by ProductDialog
      const transformedProductsData: { [key: string]: ProductHierarchy } = {};
      
      if (data.products && data.products.length > 0) {
        // Group products by product_group (LOB)
        data.products.forEach((product: any) => {
          const groupName = product.product_group || 'Other';
          
          // Create LOB entry if it doesn't exist
          if (!transformedProductsData[groupName]) {
            transformedProductsData[groupName] = {
              name: groupName,
              metrics: {
                ftm: {
                  total_base: Number(product.ftm.total_leads),
                  conversion: Number(product.ftm.converted),
                  conv_rate: Number(product.ftm.conversion_rate)
                },
                aod: {
                  total_base: Number(product.aod.total_leads),
                  conversion: Number(product.aod.converted),
                  conv_rate: Number(product.aod.conversion_rate)
                }
              },
              products: {}
            };
          }
          
          // Add sub_products directly as products under this LOB
          if (product.sub_products && product.sub_products.length > 0) {
            product.sub_products.forEach((subProduct: any) => {
              const subProductName = subProduct.sub_product;
              
              // Create entry for each sub-product as a product under this LOB
              if (transformedProductsData[groupName].products) {
                transformedProductsData[groupName].products[subProductName] = {
                  name: subProductName,
                  mis_product: subProductName,
                  metrics: {
                    ftm: {
                      total_base: Number(subProduct.ftm.total_leads),
                      conversion: Number(subProduct.ftm.converted),
                      conv_rate: Number(subProduct.ftm.conversion_rate)
                    },
                    aod: {
                      total_base: Number(subProduct.aod.total_leads),
                      conversion: Number(subProduct.aod.converted),
                      conv_rate: Number(subProduct.aod.conversion_rate)
                    }
                  },
                  rnyears: {} // Empty as there are no further nested levels
                };
              }
            });
          }
        });
      }
      
      setProductData(transformedProductsData);
      setShowProducts(true);
    } catch (error) {
      console.error('Error loading product details:', error);
      // Show error in UI if needed
    }
  };

  const getExpandIconStyle = () => ({
    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
    transition: 'transform 0.3s',
    color: theme.palette.text.primary
  });

  // Get short form of role
  const getRoleShortForm = (role: string): string => {
    // Convert to uppercase first to handle mixed case
    const upperRole = role.toUpperCase();
    // Map full role names to short forms if needed
    const roleMap: Record<string, string> = {
      'BUSINESS HEAD': 'bh',
      'VERTICAL HEAD': 'vh',
      'NATIONAL SALES MANAGER': 'nsm',
      'ZONAL MANAGER': 'zm',
      'REGIONAL MANAGER': 'rm',
      'AREA MANAGER': 'am',
      'SALES HEAD': 'sh',
      'SALES MANAGER': 'sm',
      'FIELD LEVEL SUPERVISOR': 'fls'
    };
    
    // If it's already a short form (BH, VH, etc.), just return lowercase
    if (['BH', 'VH', 'NSM', 'ZM', 'RM', 'AM', 'SH', 'SM', 'FLS'].includes(upperRole)) {
      return upperRole.toLowerCase();
    }
    
    // Otherwise, try to map from full name to short form
    return roleMap[upperRole] || upperRole.toLowerCase();
  };

  return (
    <>
      <TableRow 
        hover
        data-manager-id={manager.id}
        sx={{ 
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            backgroundColor: theme.palette.action.hover
          }
        }}
      >
        {/* Name/Manager column with fixed layout */}
        <TableCell 
          sx={{ 
            position: 'relative',
            pl: 2 + level * 2,
            pr: 1,
            cursor: canExpand ? 'pointer' : 'default',
            fontWeight: 'medium',
            fontSize: '0.875rem',
            lineHeight: 1.2,
            width: '250px',
            minWidth: '250px',
            maxWidth: '250px',
            textAlign: 'left',
            borderRight: '1px solid rgba(224, 224, 224, 1)'
          }}
          onClick={canExpand ? handleExpand : undefined}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%',
            overflow: 'hidden'
          }}>
            {canExpand ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  canExpand && handleExpand();
                }}
                sx={{
                  transition: 'transform 0.3s ease',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: roleColor.border,
                  p: 0.25,
                  flexShrink: 0,
                  mr: 1
                }}
              >
                {loading ? 
                  <CircularProgress size={16} /> :
                  <ExpandMoreIcon fontSize="small" />
                }
              </IconButton>
            ) : (
              <Box sx={{ width: 24, flexShrink: 0, mr: 1 }} />
            )}
            
            <Box sx={{ 
              flexGrow: 1,
              maxWidth: 'calc(100% - 70px)', // Leave space for the role chip
              overflow: 'hidden'
            }}>
              <Typography 
                variant="body2" 
                fontWeight={500}
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  display: 'block'
                }}
              >
                {manager.name}
              </Typography>
            </Box>
            
            <Box sx={{ 
              ml: 1, 
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              {/* Desktop view (full role name) */}
              <Chip
                label={getRoleFullForm(manager.role)}
                size="small"
                sx={{
                  bgcolor: roleColor.bg,
                  color: roleColor.text,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: '20px',
                  borderRadius: '4px',
                  display: { xs: 'none', sm: 'flex' }, // Hide on mobile, show on desktop
                  maxWidth: '120px'
                }}
              />
              
              {/* Mobile view (short form) */}
              <Chip
                label={getRoleShortForm(manager.role)}
                size="small"
                sx={{
                  bgcolor: roleColor.bg,
                  color: roleColor.text,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: '20px',
                  borderRadius: '4px',
                  display: { xs: 'flex', sm: 'none' }, // Show on mobile, hide on desktop
                  maxWidth: '40px'
                }}
              />
            </Box>
          </Box>
        </TableCell>

        {/* Metric cells remain horizontal */}
        <TableCell 
          align="center"
          sx={{
            px: 1,
            py: 1,
            minWidth: '100px'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{
              writingMode: 'horizontal-tb !important',
              textOrientation: 'mixed !important'
            }}
          >
            {manager.metrics.ftm.total_base.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell 
          align="center"
          sx={{
            px: 1,
            py: 1,
            minWidth: '100px'
          }}
        >
          <Typography 
            variant="body2"
            sx={{
              writingMode: 'horizontal-tb !important',
              textOrientation: 'mixed !important'
            }}
          >
            {manager.metrics.ftm.conversion.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell 
          align="center"
          sx={{
            px: 1,
            py: 1,
            minWidth: '100px'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(manager.metrics.ftm.conv_rate, theme),
              fontWeight: 600,
              writingMode: 'horizontal-tb !important',
              textOrientation: 'mixed !important'
            }}
          >
            {manager.metrics.ftm.conv_rate.toFixed(1)}%
          </Typography>
        </TableCell>

        <TableCell 
          align="center"
          sx={{
            px: 1,
            py: 1,
            minWidth: '100px'
          }}
        >
          <Typography 
            variant="body2"
            sx={{
              writingMode: 'horizontal-tb !important',
              textOrientation: 'mixed !important'
            }}
          >
            {manager.metrics.aod.total_base.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell 
          align="center"
          sx={{
            px: 1,
            py: 1,
            minWidth: '100px'
          }}
        >
          <Typography 
            variant="body2"
            sx={{
              writingMode: 'horizontal-tb !important',
              textOrientation: 'mixed !important'
            }}
          >
            {manager.metrics.aod.conversion.toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell 
          align="center"
          sx={{
            px: 1,
            py: 1,
            minWidth: '100px'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(manager.metrics.aod.conv_rate, theme),
              fontWeight: 600,
              writingMode: 'horizontal-tb !important',
              textOrientation: 'mixed !important'
            }}
          >
            {manager.metrics.aod.conv_rate.toFixed(1)}%
          </Typography>
        </TableCell>

        {/* Action buttons remain unchanged */}
        <TableCell 
          align="center"
          sx={{
            px: 1,
            py: 1,
            minWidth: '100px'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                loadAgencyDetails();
              }}
              title="View Agency Details"
              sx={{
                color: roleColor.text === '#FFFFFF' ? roleColor.bg : roleColor.text,
                p: 0.5
              }}
            >
              <BusinessIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                loadProductDetails();
              }}
              title="View Product Details"
              sx={{
                color: roleColor.text === '#FFFFFF' ? roleColor.bg : roleColor.text,
                p: 0.5
              }}
            >
              <AssessmentIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expanded subordinates section - also fix layout issues here */}
      {isExpanded && canExpand && (
        <TableRow>
          <TableCell 
            colSpan={8} 
            sx={{ 
              p: 0, 
              border: 'none',
              bgcolor: `${roleColor.bg}04`,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: level * 2 + 28,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: `${roleColor.border}40`
              }
            }}
          >
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : error ? (
                  <Typography color="error">{error}</Typography>
                ) : subordinates.length > 0 ? (
                  <Table size="small" sx={{ 
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': {
                      px: 1,
                      py: 0.75,
                      borderColor: theme.palette.divider,
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    },
                    '& .MuiTableRow-root:nth-of-type(odd)': {
                      backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    },
                    '& .MuiTableCell-root:first-of-type': {
                      borderLeft: 'none',
                    },
                    '& .MuiTableCell-root:last-of-type': {
                      borderRight: 'none',
                    }
                  }}>
                    <colgroup>
                      <col style={{ width: '250px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                    </colgroup>

                    <TableBody>
                      {subordinates.map((subordinate) => (
                        <ManagerRow 
                          key={subordinate.id} 
                          manager={subordinate} 
                          level={level + 1}
                          region={region}
                          onLoadSubordinates={onLoadSubordinates}
                          parentId={manager.id}
                          expandedSubordinates={expandedSubordinates}
                          toggleSubordinate={toggleSubordinate}
                          filters={filters}
                          columnWidths={columnWidths}
                        />
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                    No subordinates found
                  </Typography>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      {/* Dialogs remain unchanged */}
      <AgencyDialog 
        open={showAgency} 
        onClose={() => setShowAgency(false)} 
        agencyDetails={agencyDetails} 
      />

      <ProductDialog 
        open={showProducts} 
        onClose={() => setShowProducts(false)} 
        productData={productData} 
      />
    </>
  );
};

export const RegionManagerDrillDown: React.FC<RegionManagerDrillDownProps> = ({ 
  region, 
  initialData,
  filters,
  compact = false
}): React.ReactElement => {
  const theme = useTheme();
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [managerData, setManagerData] = useState<ManagerBreakdownResponse | null>(initialData || null);
  const [expandedSubordinates, setExpandedSubordinates] = useState<Set<string>>(new Set());
  const [subordinateData, setSubordinateData] = useState<Record<string, ManagerData[]>>({});
  const [loadingSubordinates, setLoadingSubordinates] = useState<Record<string, boolean>>({});
  const [subordinateErrors, setSubordinateErrors] = useState<Record<string, string | null>>({});
  const [prevFilters, setPrevFilters] = useState(filters);

  // Initial data load
  useEffect(() => {
    if (!initialData) {
      loadData();
    } else {
      setManagerData(initialData);
    }
  }, [initialData, region]);

  // Handle filter changes - always reload on filter changes
  useEffect(() => {
    // Check if filters have actually changed
    const filterString = JSON.stringify(filters);
    const prevFilterString = JSON.stringify(prevFilters);
    
    if (filterString !== prevFilterString) {
      setPrevFilters(filters);
      // Collapse all expanded rows and clear subordinate data
      setExpandedSubordinates(new Set());
      setSubordinateData({});
      setLoadingSubordinates({});
      setSubordinateErrors({});
      // If we have filters and managerData exists, reload data regardless of initialData
      if (filters) {
        loadData();
      }
    }
  }, [
    filters?.selectedMonths,
    filters?.selectedYears,
    filters?.selectedLOB?.join(','),
    filters?.selectedChannels?.join(','),
    filters?.selectedSubChannels?.join(','),
    filters?.selectedProducts?.join(','),
    filters?.dateRange?.startDate,
    filters?.dateRange?.endDate
  ]);

  const toggleSubordinate = async (id: string) => {
    const newExpandedSet = new Set(expandedSubordinates);
    if (newExpandedSet.has(id)) {
      newExpandedSet.delete(id);
    } else {
      newExpandedSet.add(id);
      // Always load subordinate data when expanding, even if cached
      await loadSubordinateData(id);
    }
    setExpandedSubordinates(newExpandedSet);
    
  };

  const loadSubordinateData = async (employeeId: string) => {
    // Find the subordinate from the manager data
    const subordinate = managerData?.subordinates.find(s => s.employee_id === employeeId);
    if (!subordinate) return;
    
    // Set loading state
    setLoadingSubordinates(prev => ({ ...prev, [employeeId]: true }));
    setSubordinateErrors(prev => ({ ...prev, [employeeId]: null }));
    
    try {
      // Build hierarchy path
      let hierarchyPath = subordinate.hierarchy_path || '';
      
      // If no existing hierarchy path, create one starting with this subordinate
      if (!hierarchyPath) {
        hierarchyPath = `${subordinate.employee_role}:${subordinate.employee_id}`;
      }
      
      // Call the loadSubordinates function
      const data = await loadSubordinates(
        subordinate.employee_id,
        subordinate.employee_role,
        hierarchyPath
      );
      
      // Save the data
      setSubordinateData(prev => ({ ...prev, [employeeId]: data }));
    } catch (err) {
      console.error('Error loading subordinate data:', err);
      setSubordinateErrors(prev => ({ ...prev, [employeeId]: 'Failed to load subordinates' }));
    } finally {
      setLoadingSubordinates(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Convert filter values to the proper types
      const apiFilters: any = {};
      
      if (filters) {
        // Convert selectedMonths to string format expected by buildFilterParams
        if (filters.selectedMonths) {
          if (Array.isArray(filters.selectedMonths)) {
            apiFilters.selectedMonth = filters.selectedMonths[0]?.toString() || '';
          } else {
            apiFilters.selectedMonth = String(filters.selectedMonths);
          }
        }
        
        // Convert selectedYears to string format expected by buildFilterParams
        if (filters.selectedYears) {
          if (Array.isArray(filters.selectedYears)) {
            apiFilters.selectedYear = filters.selectedYears[0]?.toString() || '';
          } else {
            apiFilters.selectedYear = String(filters.selectedYears);
          }
        }
        
        // Copy other filter properties
        if (filters.selectedLOB) apiFilters.selectedLOB = filters.selectedLOB;
        if (filters.selectedProducts) apiFilters.selectedProducts = filters.selectedProducts;
        if (filters.selectedChannels) apiFilters.selectedChannels = filters.selectedChannels;
        if (filters.selectedSubChannels) apiFilters.selectedSubChannels = filters.selectedSubChannels;
        if (filters.dateRange) apiFilters.dateRange = filters.dateRange;
        if (filters.selectedRegion) apiFilters.selectedRegion = filters.selectedRegion;
        if (filters.selectedSubProducts) apiFilters.selectedSubProducts = filters.selectedSubProducts;
      }
      
      // Don't include empty employee_id parameter
      // Always use short form role codes (bh, vh, nsm, etc.) for API calls
      const response = await fetchManagerBreakdown(
        undefined, // Don't pass employee_id parameter when it's empty
        undefined, // Default role should be Business Head short form
        '[]', // Default hierarchy path
        apiFilters
      );

      
      setManagerData(response);
    } catch (err) {
      console.error('Error loading manager data:', err);
      setError('Failed to load manager data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSubordinates = async (managerId: string, managerRole: string, hierarchyPath?: string) => {
    try {
      // Get the next level based on current manager's role
      const hierarchyLevels = ['BH', 'VH', 'NSM', 'ZM', 'RM', 'AM', 'SH', 'SM', 'FLS'];
      const currentIndex = hierarchyLevels.indexOf(managerRole.toUpperCase());
      const nextLevel = hierarchyLevels[currentIndex + 1];
      
      if (!nextLevel) {
        // If there's no next level, return empty array
        return [];
      }
      
      // Build hierarchy path if not provided
      let effectiveHierarchyPath = hierarchyPath || `${managerRole}:${managerId}`;
      
      // Convert string arrays to number arrays for months and years
      const apiFilters: any = {};
      
      if (filters) {
        // Convert selectedMonths to string format expected by buildFilterParams
        if (filters.selectedMonths) {
          if (Array.isArray(filters.selectedMonths)) {
            apiFilters.selectedMonth = filters.selectedMonths[0]?.toString() || '';
          } else {
            apiFilters.selectedMonth = String(filters.selectedMonths);
          }
        }
        
        // Convert selectedYears to string format expected by buildFilterParams
        if (filters.selectedYears) {
          if (Array.isArray(filters.selectedYears)) {
            apiFilters.selectedYear = filters.selectedYears[0]?.toString() || '';
          } else {
            apiFilters.selectedYear = String(filters.selectedYears);
          }
        }
        
        // Copy other filter properties for manager breakdown
        if (filters.selectedLOB) apiFilters.selectedLOB = filters.selectedLOB;
        if (filters.selectedProducts) apiFilters.selectedProducts = filters.selectedProducts;
        if (filters.selectedChannels) apiFilters.selectedChannels = filters.selectedChannels;
        if (filters.selectedSubChannels) apiFilters.selectedSubChannels = filters.selectedSubChannels;
        if (filters.dateRange) apiFilters.dateRange = filters.dateRange;
        if (filters.selectedSubProducts) apiFilters.selectedSubProducts = filters.selectedSubProducts;
        
        if (filters.selectedRegion) apiFilters.selectedRegion = filters.selectedRegion;
      }
      
      // Map role names to their short forms
      const getRoleShortForm = (role: string): string => {
        // Convert to uppercase first to handle mixed case
        const upperRole = role.toUpperCase();
        // Map full role names to short forms if needed
        const roleMap: Record<string, string> = {
          'BUSINESS HEAD': 'bh',
          'VERTICAL HEAD': 'vh',
          'NATIONAL SALES MANAGER': 'nsm',
          'ZONAL MANAGER': 'zm',
          'REGIONAL MANAGER': 'rm',
          'AREA MANAGER': 'am',
          'SALES HEAD': 'sh',
          'SALES MANAGER': 'sm',
          'FIELD LEVEL SUPERVISOR': 'fls'
        };
        
        // If it's already a short form (BH, VH, etc.), just return lowercase
        if (['BH', 'VH', 'NSM', 'ZM', 'RM', 'AM', 'SH', 'SM', 'FLS'].includes(upperRole)) {
          return upperRole.toLowerCase();
        }
        
        // Otherwise, try to map from full name to short form
        return roleMap[upperRole] || upperRole.toLowerCase();
      };
      
      // Get the short form of the role
      const roleShortForm = getRoleShortForm(managerRole);
      
      // Call the manager breakdown API with the current manager's role (not the next level)
      // This is critical - we want to get the subordinates of the current manager
      const response = await fetchManagerBreakdown(
        managerId, 
        roleShortForm, 
        effectiveHierarchyPath,
        apiFilters
      );
      
      // Transform the response to match the expected format
      const subordinates: ManagerData[] = response.subordinates.map(sub => ({
        id: sub.employee_id,
        name: sub.employee_name,
        role: sub.employee_role,
        channel: null,
        state: null,
        metrics: {
          ftm: {
            total_base: Number(sub.ftm.total_leads),
            conversion: Number(sub.ftm.converted),
            conv_rate: Number(sub.ftm.conversion_rate)
          },
          aod: {
            total_base: Number(sub.aod.total_leads),
            conversion: Number(sub.aod.converted),
            conv_rate: Number(sub.aod.conversion_rate)
          }
        },
        hierarchy_path: sub.hierarchy_path
      }));
      
      return subordinates;
    } catch (error) {
      console.error('Error loading subordinates:', error);
      throw error;
    }
  };

  const handleExport = () => {
    if (!managerData) return;

    // Collection for our final export data
    let allExportData: any[] = [];
    
    
    // Helper function to collect all data from the React component tree
    const collectAllManagerData = () => {
      // Start with top-level subordinates
      const exportData = managerData.subordinates.map(sub => ({
        'Employee ID': sub.employee_id,
        'Employee Name': sub.employee_name,
        'Role': sub.employee_role,
        'FTM - Total Base': sub.ftm.total_leads,
        'FTM - Converted': sub.ftm.converted,
        'FTM - Conv. Rate': `${sub.ftm.conversion_rate}%`,
        'AOD - Total Base': sub.aod.total_leads,
        'AOD - Converted': sub.aod.converted,
        'AOD - Conv. Rate': `${sub.aod.conversion_rate}%`
      }));
      
      // Use DOM to find expanded rows (fallback approach)
      try {
        // Find all rows with manager data attributes
        const managerRows = document.querySelectorAll('tr[data-manager-id]');
        
        // For each manager row, check if it has expanded content
        managerRows.forEach(row => {
          const managerId = row.getAttribute('data-manager-id');
          if (!managerId) return;
          
          // Find the next row that might contain expanded content
          const nextRow = row.nextElementSibling;
          if (nextRow && nextRow.querySelector('.MuiCollapse-entered')) {
            
            // Look for table rows inside the expanded content
            const childRows = nextRow.querySelectorAll('tr[data-manager-id]');
            childRows.forEach(childRow => {
              const childId = childRow.getAttribute('data-manager-id');
              if (!childId) return;
              
              // Find the manager name in the row
              const nameCell = childRow.querySelector('td');
              if (!nameCell) return;
              
              const nameText = nameCell.querySelector('.MuiTypography-body2')?.textContent?.trim();
              const roleText = nameCell.querySelector('.MuiChip-label')?.textContent?.trim();
              
              // Get all cells for metrics data
              const cells = childRow.querySelectorAll('td');
              if (cells.length < 7) return;
              
              // Add this child manager to the export data
              exportData.push({
                'Employee ID': childId,
                'Employee Name': nameText || '',
                'Role': roleText || '',
                'FTM - Total Base': cells[1]?.textContent?.trim() || '0',
                'FTM - Converted': cells[2]?.textContent?.trim() || '0',
                'FTM - Conv. Rate': cells[3]?.textContent?.trim() || '0%',
                'AOD - Total Base': cells[4]?.textContent?.trim() || '0',
                'AOD - Converted': cells[5]?.textContent?.trim() || '0',
                'AOD - Conv. Rate': cells[6]?.textContent?.trim() || '0%'
              });
            });
          }
        });
      } catch (error) {
        console.error("Error collecting expanded rows:", error);
      }
      
      return exportData;
    };
    
    // Collect all data and export
    allExportData = collectAllManagerData();
    
    // Convert to CSV
    downloadAsCSV(allExportData, `manager_performance_${region.toLowerCase()}.csv`);
  };

  // Define fixed column widths to be used consistently across all tables
  const columnWidths = {
    name: '25%',
    ftmBase: '12.5%',
    ftmConv: '12.5%',
    ftmRate: '12.5%',
    aodBase: '12.5%',
    aodConv: '12.5%',
    aodRate: '12.5%',
    actions: '12.5%'
  };

  // Function to download data as CSV
  const downloadAsCSV = (data: any[], filename: string) => {
    // Skip if no data
    if (!data || !data.length) return;
    
    // Convert object array to CSV string
    const replacer = (_key: string, value: any) => value === null ? '' : value;
    const header = Object.keys(data[0]);
    const csv = [
      header.join(','),
      ...data.map(row => 
        header.map(fieldName => 
          JSON.stringify(row[fieldName], replacer)
        ).join(',')
      )
    ].join('\r\n');
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!managerData) {
    return (
      <Typography variant="body1" sx={{ py: 2 }}>
        {loading ? 'Loading manager data...' : error || 'No manager data available'}
      </Typography>
    );
  }

  return (
    <Card>
      <CardContent sx={{ 
        p: compact ? 1 : 2,
        '&:last-child': { pb: compact ? 1 : 2 }
      }}>
        {/* Hidden button for external triggering of export */}
        <IconButton
          id="region-manager-export-btn"
          onClick={handleExport}
          sx={{ display: 'none' }}
        >
          <FileDownloadIcon />
        </IconButton>
        <Box sx={{ height: 'auto', width: '100%' }}>
          <TableContainer sx={{ maxHeight: compact ? 300 : 400, overflow: 'auto' }}>
            <Table sx={{ 
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': {
                px: 1,
                py: 1,
                borderColor: theme.palette.divider,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                borderTop: 'none',
              },
              '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
              '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTableCell-root:first-of-type': {
                borderLeft: 'none',
              },
              '& .MuiTableCell-root:last-of-type': {
                borderRight: 'none',
              }
            }} size={compact ? "small" : "medium"} stickyHeader>
              <colgroup>
                <col style={{ width: '250px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
              </colgroup>
              
              {/* Rest of the table remains unchanged */}
              <TableHead>
                <TableRow>
                  <TableCell 
                    rowSpan={2}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Manager
                  </TableCell>
                  <TableCell 
                    align="center"
                    colSpan={3}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    FTM
                  </TableCell>
                  <TableCell 
                    align="center"
                    colSpan={3}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    AOD
                  </TableCell>
                  <TableCell 
                    rowSpan={2}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
                <TableRow>
                  {/* Total Base, Converted, Conv. Rate subheaders for FTM */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Total Base
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Converted
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Conv. Rate
                  </TableCell>
                  
                  {/* Total Base, Converted, Conv. Rate subheaders for AOD */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Total Base
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Converted
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Conv. Rate
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Generate manager rows from data */}
                {managerData.subordinates.map((subordinate) => {
                  // Create a ManagerData object from the subordinate
                  const manager: ManagerData = {
                    id: subordinate.employee_id,
                    name: subordinate.employee_name,
                    role: subordinate.employee_role,
                    channel: null,
                    state: null,
                    metrics: {
                      ftm: {
                        total_base: Number(subordinate.ftm.total_leads),
                        conversion: Number(subordinate.ftm.converted),
                        conv_rate: Number(subordinate.ftm.conversion_rate)
                      },
                      aod: {
                        total_base: Number(subordinate.aod.total_leads),
                        conversion: Number(subordinate.aod.converted),
                        conv_rate: Number(subordinate.aod.conversion_rate)
                      }
                    },
                    hierarchy_path: subordinate.hierarchy_path
                  };
                  
                  // Convert filters to match the ManagerRow filters type
                  const managerRowFilters = filters ? {
                    selectedMonths: Array.isArray(filters.selectedMonths) 
                      ? filters.selectedMonths.map(m => typeof m === 'string' ? parseInt(m, 10) : m)
                      : filters.selectedMonths 
                        ? [typeof filters.selectedMonths === 'string' ? parseInt(filters.selectedMonths, 10) : filters.selectedMonths]
                        : undefined,
                    selectedYears: Array.isArray(filters.selectedYears)
                      ? filters.selectedYears.map(y => typeof y === 'string' ? parseInt(y, 10) : y)
                      : filters.selectedYears
                        ? [typeof filters.selectedYears === 'string' ? parseInt(filters.selectedYears, 10) : filters.selectedYears]
                        : undefined,
                    selectedLOB: filters.selectedLOB,
                    selectedProducts: filters.selectedProducts?.map(p => typeof p === 'string' ? p : (typeof p === 'object' ? p.product_name : p)) as string[] | undefined,
                    selectedChannels: filters.selectedChannels,
                    selectedSubChannels: filters.selectedSubChannels,
                    dateRange: filters.dateRange,
                    selectedRegion: filters.selectedRegion ? (Array.isArray(filters.selectedRegion) ? filters.selectedRegion : [filters.selectedRegion]) : undefined,
                    selectedSubProducts: filters.selectedSubProducts
                  } : undefined;
                  
                  return (
                  <ManagerRow 
                    key={manager.id} 
                    manager={manager} 
                    level={0}
                    region={region}
                    onLoadSubordinates={loadSubordinates}
                    parentId={manager.id}
                    expandedSubordinates={expandedSubordinates}
                    toggleSubordinate={toggleSubordinate}
                    filters={managerRowFilters}
                    columnWidths={columnWidths}
                  />
                  );
                })}
                
                {/* Total Row for all Business Heads */}
                {managerData.subordinates.length > 0 && (
                  <TableRow 
                    sx={{ 
                      bgcolor: theme.palette.grey[50],
                      borderTop: `2px solid ${theme.palette.divider}`,
                      '& .MuiTableCell-root': {
                        fontWeight: 'bold',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: theme.palette.grey[50],
                        zIndex: 1,
                        py: 1.5,
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        TOTAL
                      </Typography>
                    </TableCell>
                    
                    {/* FTM Metrics */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {Number(managerData.ftm?.total_leads || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {Number(managerData.ftm?.converted || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          color: getConversionColor(Number(managerData.ftm?.conversion_rate || 0), theme)
                        }}
                      >
                        {Number(managerData.ftm?.conversion_rate || 0).toFixed(1)}%
                      </Typography>
                    </TableCell>
                    
                    {/* AOD Metrics */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {Number(managerData.aod?.total_leads || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {Number(managerData.aod?.converted || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          color: getConversionColor(Number(managerData.aod?.conversion_rate || 0), theme)
                        }}
                      >
                        {Number(managerData.aod?.conversion_rate || 0).toFixed(1)}%
                      </Typography>
                    </TableCell>
                    
                    {/* Empty cell for actions column */}
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </CardContent>
    </Card>
  );
};